import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  colors,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import React from 'react'
import { useAppStore, useSnackbars } from '@assembly/hooks'
import { getContract, getProfile } from '@assembly/api/contractor'
import SignContractDialog from './SignContractDialog'
import { getApiErrorMsg, getMediaUrl } from '@assembly/utils'
import { SnackbarType } from '@assembly/contexts'
import logo from '@assembly/assets/logo.png'
import axios from 'axios'
import DownloadIcon from '@mui/icons-material/Download'
import CloseIcon from '@mui/icons-material/Close'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/TextLayer.css'

// Initialize PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`

const StyledPaper = styled(Paper)(() => ({
  padding: '0 15px 20px 15px',
  border: '1px solid',
  borderColor: colors.grey[200],
  borderRadius: '10px',
  boxShadow: 'none',
}))

const AccepButtonBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginTop: '40px',
  marginBottom: '40px',
}))

const AcceptButton = styled(Button)(({ theme }) => ({
  padding: '12px 24px',
  fontSize: '1.1rem',
  minWidth: '200px',
}))

const SignatureImg = styled('img')(() => ({
  width: '100px',
  height: 'auto',
  marginBottom: '5px',
}))

const SignatureOuterBox = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'flex-end',
  paddingRight: '300px',
}))

const SignatureBox = styled(Box)(() => ({
  display: 'flex'
}))

const Logo = styled('img')(() => ({
  width: '150px',
  height: 'auto',
  marginTop: '20px',
}))

const ContractSignedInfo = styled(Box)(() => ({
  padding: '20px',
  textAlign: 'center',
}))

const InfoItem = styled(Typography)(() => ({
  margin: '10px 0',
  fontSize: '16px',
}))

const PDFViewer = ({ pdfURL }: { pdfURL: string }) => {
  const [numPages, setNumPages] = React.useState<number>()

  function onDocumentLoadSuccess({ numPages }: { numPages: number }): void {
    setNumPages(numPages)
  }

  return (
    <Document file={pdfURL} onLoadSuccess={onDocumentLoadSuccess}>
      {Array.from(new Array(numPages), (el, index) => (
        <Box key={`pdf-page-${index}`}>
          <Page
            renderAnnotationLayer={false}
            width={850}
            key={`page_${index + 1}`}
            pageNumber={index + 1}
            renderTextLayer={false}
          />
          <Divider />
        </Box>
      ))}
    </Document>
  )
}

const ContractViewerDialog = ({
  open,
  onClose,
  contractURL,
}: {
  open: boolean
  onClose: () => void
  contractURL: string
}) => {
  const handleDownload = () => {
    window.open(contractURL, '_blank')
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        Contract
        <Box sx={{ position: 'absolute', right: 8, top: 8, display: 'flex', gap: 1 }}>
          <IconButton
            aria-label="download"
            onClick={handleDownload}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <DownloadIcon />
          </IconButton>
          {onClose ? (
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          ) : null}
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <PDFViewer pdfURL={contractURL} />
      </DialogContent>
    </Dialog>
  )
}

export default function Contract() {
  const didMountRef = React.useRef(false)
  const [loading, setIsLoading] = React.useState<boolean>(false)
  const { store, setContractorProfile, setContract, setSignatureBase64 } =
    useAppStore()
  const [signContractDialogOpen, setSignContractDialogOpen] =
    React.useState<boolean>(false)
  const [contractIsSigned, setContractIsSigned] =
    React.useState<boolean>(false)
  const [signedContractTS, setSignedContractTS] = React.useState<string>('')
  const [signedContractIP, setSignedContractIP] = React.useState<string>('')
  const [resourceContractVersion, setResourceContractVersion] = React.useState<string>('')
  const [pdfViewerOpen, setPdfViewerOpen] = React.useState<boolean>(false)
  const { addAlert } = useSnackbars()

  React.useEffect(() => {
    if (!didMountRef.current) {
      if (!store.isContractorProfileLoaded || !store.isContractLoaded) {
        getProfileAsync()
      } else {
        setIsLoading(false)
      }
      didMountRef.current = true
    }
  }, [])

  const getProfileAsync = async () => {
    try {
      setIsLoading(true)
      const { data } = await getProfile()
      const res = await getContract()

      // Set contract signature metadata if available
      if (data.contractSignature && data.contractSignature.media) {
        const base64Data = await fetchImageAndConvertToBase64(
          getMediaUrl(data.contractSignature.media) as string
        )
        setSignatureBase64(base64Data)
      }

      // Check for signing metadata in both profile and contract data
      if (res.data.signedContractTS) {
        setSignedContractTS(res.data.signedContractTS)
      } else if (data.signedContractTS) {
        setSignedContractTS(data.signedContractTS)
      }

      if (res.data.signedContractIP) {
        setSignedContractIP(res.data.signedContractIP)
      } else if (data.signedContractIP) {
        setSignedContractIP(data.signedContractIP)
      }

      // Look for resourceContractVersion in both responses
      if (res.data.resourceContractVersion) {
        setResourceContractVersion(res.data.resourceContractVersion)
      } else if (data.resourceContractVersion) {
        setResourceContractVersion(data.resourceContractVersion)
      } else {
        setResourceContractVersion('');
      }

      setContract(res.data.message)
      setContractorProfile(data)
      setContractIsSigned(data.hasSignedContract)
      setIsLoading(false)
    } catch (error: any) {
      setIsLoading(false)
      addAlert({
        message: getApiErrorMsg(error),
        type: SnackbarType.Error,
      })
    }
  }

  const handleClickAccept = () => {
    setSignContractDialogOpen(true)
  }

  const handleCloseSignContractDialog = () => {
    setSignContractDialogOpen(false)
  }

  const handleSuccess = (data: any) => {
    setContractorProfile(data)
    setContractIsSigned(true)

    // Reload the contract data to get the updated metadata
    getProfileAsync()
  }

  const fetchImageAndConvertToBase64 = async (url: string) => {
    try {
      const response = await axios.get(url, { responseType: 'blob' })

      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => {
          const base64data = reader.result
          resolve(base64data)
        }
        reader.onerror = () => {
          reject(new Error('Failed to convert blob to base64'))
        }
        reader.readAsDataURL(response.data)
      })
    } catch (error: any) {
      console.error('There was a problem with the fetch operation:', error)
    }
  }

  const handleViewPDF = () => {
    setPdfViewerOpen(true)
  }

  const handleClosePDFViewer = () => {
    setPdfViewerOpen(false)
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      {contractIsSigned && resourceContractVersion ? (
        <StyledPaper>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleViewPDF}
              startIcon={<DownloadIcon />}
            >
              View Signed Contract
            </Button>
          </Box>
        </StyledPaper>
      ) : (
        <StyledPaper>
          {store.contract && (
            <React.Fragment>
              <Logo src={logo} alt="logo" />
              <div dangerouslySetInnerHTML={{ __html: store.contract }} />
            </React.Fragment>
          )}
          <SignatureOuterBox>
            <SignatureBox>
              <SignatureImg
                src={getMediaUrl(
                  store.contractorProfile.contractSignature?.media || ''
                )}
              />
            </SignatureBox>
          </SignatureOuterBox>
        </StyledPaper>
      )}

      {!contractIsSigned && (
        <AccepButtonBox>
          <AcceptButton
            variant="contained"
            color="primary"
            onClick={handleClickAccept}
          >
            Accept Contract
          </AcceptButton>
        </AccepButtonBox>
      )}

      {contractIsSigned && (
        <ContractSignedInfo>
          <InfoItem>
            Contract signed on: {signedContractTS}
          </InfoItem>
          <InfoItem>IP Address: {signedContractIP}</InfoItem>
          <InfoItem>
            Contract Version: {resourceContractVersion}
          </InfoItem>
        </ContractSignedInfo>
      )}

      <SignContractDialog
        open={signContractDialogOpen}
        onClose={handleCloseSignContractDialog}
        onSuccess={handleSuccess}
      />

      {store.contractorProfile.contractSignature?.media && (
        <ContractViewerDialog
          open={pdfViewerOpen}
          onClose={handleClosePDFViewer}
          contractURL={getMediaUrl(store.contractorProfile.contractSignature.media) as string}
        />
      )}
    </Box>
  )
}
